import { get } from 'shared/util/helpers';
import set from 'lodash/set';

import { EventNameTypes, makeSegmentTrackCall, trackInteraction } from 'client/javascript/analytics';
import { trackingEventsLabels } from 'client/self_serve/shared/constants';
import { SELF_SERVE_STEP_PATHNAMES, MAX_DAYS_AWAY_TO_ALLOW_MOVE_IN } from 'shared/util/constants';

export const DATE_TOO_FAR_IN_FUTURE_LABEL = `Self Serve - Select - Over ${MAX_DAYS_AWAY_TO_ALLOW_MOVE_IN} days`;
export const INVALID_DATE_LABEL = 'Self Serve - Select - Invalid Date';
export const RESULTS_LENGTH_LABEL = 'Self Serve - Select - Results Length';
export const MAX_CAPACITY_LABEL = 'Self Serve - Select - Filter Applied over 20';
export const FILTER_APPLIED_LABEL = 'Self Serve - Select - Filter - Applied';
export const OVERLAY_OPEN_LABEL = 'Self Serve - Select - Filter - Open';
export const CAPACITY_OVERLAY_CLEARED_LABEL = 'Self Serve - Select - capacity filter cleared';
export const CAPACITY_OVERLAY_INTERACTION_LABEL = 'Self Serve - Select - capacity changed';
export const DATEPICKER_FILTER_NAME = 'datepicker';
export const CAPACITY_FILTER_NAME = 'capacity';
export const PRIVATE_OFFICE_DETAILS_LABEL = 'Self Serve - Select - Click - Private Office';
export const DEDICATED_DESK_DETAILS_LABEL = 'Self Serve - Select - Click - Dedicated Desk';
export const HOT_DESK_DETAILS_LABEL = 'Self Serve - Select - Click - Hot Desk';
export const ALL_ACCESS_DETAILS_LABEL = 'Self Serve - Select - Click - All Access';
export const WORKSPACE_NEARBY_LABEL = 'Self Serve - Select - Click - Workspace Nearby';
export const NO_RESULTS_LABEL = 'Self Serve - Select - Greyed Out - {productType}';

export const TRACK_ACTIVITY_FIELDS = [
  {
    key: 'reservable_uuid',
    contextPath: 'reservablesUuids.0'
  },
  {
    key: 'currency',
    contextPath: 'currency'
  },
  {
    key: 'cbpDiscount',
    contextPath: 'cbpDiscount'
  },
  {
    key: 'cbpDiscountPercentage',
    contextPath: 'cbpDiscountPercentage'
  },
  {
    key: 'startDate',
    contextPath: 'selectionInput.startDate'
  },
  {
    key: 'capacity',
    contextPath: 'selectionInput.capacity'
  },
  {
    key: 'amount',
    contextPath: 'monthlyPrice'
  }
];

export class StepTimer {
  constructor(step) {
    this.step = step;
  }

  start() {
    this.startTime = new Date();
  }

  getElapsedTime() {
    const endTime = new Date();

    return endTime - this.startTime;
  }
}

export class StepsNavigatorAnalytics {
  constructor() {
    this._handleBeforeUnload = this.sendStepTimingEvent.bind(this);
  }

  setup() {
    window.addEventListener('beforeunload', this._handleBeforeUnload);
  }

  teardown() {
    window.removeEventListener('beforeunload', this._handleBeforeUnload);
  }

  startStepTiming(step) {
    this.stepTimer = new StepTimer(step);
    this.stepTimer.start();
  }

  sendStepTimingEvent = () => {
    const elapsedTimeInMilliseconds = this.stepTimer.getElapsedTime();
    const { step } = this.stepTimer;

    const props = {
      step: step.index,
      path: SELF_SERVE_STEP_PATHNAMES[step.index],
      startTime: this.stepTimer.start,
      elapsedTimeInMilliseconds,
      label: `${trackingEventsLabels.timeOnStep}: ${step.name}`
    };

    makeSegmentTrackCall(EventNameTypes.timing, props);
  };

  // eslint-disable-next-line class-methods-use-this
  sendStepViewEvent(step) {
    const props = {
      step: step.index,
      label: `${trackingEventsLabels.stepView}: ${step.name}`
    };

    makeSegmentTrackCall(EventNameTypes.interaction, props);
    // TODO: Figure out exactly what this is for
    // newrelic.addPageAction(EventNameTypes.interaction, props);
  }

  // eslint-disable-next-line class-methods-use-this
  trackActivity(step, context) {
    const props = StepsNavigatorAnalytics.getTrackActivityPayload(step, context);

    makeSegmentTrackCall(EventNameTypes.signupActivity, props);
  }

  // eslint-disable-next-line class-methods-use-this
  sendBreadcrumbClickEvent(fromStep, to) {
    const label = trackingEventsLabels.breadcrumbs.replace('{stepName}', fromStep.name);

    const props = {
      label,
      from: fromStep,
      ...to
    };

    trackInteraction(props);
  }

  trackSignUpLoginToggle = isLoginForm => {
    const trackInteractionLabel = isLoginForm ? trackingEventsLabels.signUpLink : trackingEventsLabels.loginLink;
    trackInteraction({
      label: trackInteractionLabel
    });
  };

  static getTrackActivityPayload(step, context) {
    const label = trackingEventsLabels.stepCompletion.replace('{stepName}', step.name);

    // Only pull fields intended to be reported from the context. Exclude fields like password, password_confirmation.
    const cleanedContext = TRACK_ACTIVITY_FIELDS.reduce((memo, { contextPath, key }) => {
      const value = get(context, contextPath, undefined);

      if (value !== undefined) {
        set(memo, key, value);
      }

      return memo;
    }, {});

    return {
      ...cleanedContext,
      label,
      step: step.index
    };
  }
}
